import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { Users } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Professor {
    id: number;
    name: string;
    bio: string | null;
    avatar_url: string | null;
    sections_list: string[];
}

interface Props {
    professors: Professor[];
}

const ProfessorsGalleryContent: React.FC<Props> = ({ professors }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    return (
        <AppLayout>
            <PageTitle title={t('professors.pageTitle')} />
            <div className="mx-auto max-w-6xl">
                {/* Page Header */}
                <div className="mb-12 text-center">
                    <div className={`mx-auto mb-6 h-1 w-16 rounded-full bg-gradient-to-r ${colors.gradientFrom} ${colors.gradientTo}`} />
                    <h1 className="mb-3 text-3xl font-bold text-gray-900">
                        {t('professors.title')}
                    </h1>
                    <p className="text-gray-600">
                        {t('professors.description')}
                    </p>
                </div>

                {/* Professors Grid */}
                {professors.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="py-12 text-center">
                            <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                            <h3 className="mb-2 text-xl font-semibold text-gray-900">
                                {t('professors.noProfessorsTitle')}
                            </h3>
                            <p className="text-gray-600">
                                {t('professors.noProfessorsDescription')}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                        {professors.map((professor) => (
                            <div
                                key={professor.id}
                                className="flex flex-col items-center text-center"
                            >
                                {/* Avatar with gradient ring */}
                                <div
                                    className={`mb-5 rounded-full bg-gradient-to-br p-1 ${colors.gradientFrom} ${colors.gradientVia} ${colors.gradientTo}`}
                                >
                                    <div className="rounded-full bg-white p-1">
                                        <img
                                            src={professor.avatar_url || ''}
                                            alt={professor.name}
                                            className="h-32 w-32 rounded-full object-cover"
                                        />
                                    </div>
                                </div>

                                {/* Name */}
                                <h3 className="mb-3 text-xl font-bold text-gray-900">
                                    {professor.name}
                                </h3>

                                {/* Bio */}
                                {professor.bio && (
                                    <p className="mb-4 text-sm leading-relaxed text-gray-600">
                                        {professor.bio}
                                    </p>
                                )}

                                {/* Sections */}
                                {professor.sections_list.length > 0 && (
                                    <p
                                        className={`text-sm font-medium ${colors.iconColor}`}
                                    >
                                        {t('professors.teachesIn')}{' '}
                                        {professor.sections_list.join(', ')}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const ProfessorsGallery: React.FC<Props> = ({ professors }) => (
    <ThemeWrapper>
        <ProfessorsGalleryContent professors={professors} />
    </ThemeWrapper>
);

export default ProfessorsGallery;
